import { Box, Typography } from "@mui/material";
import React from "react";

import { useFeatureFlagEnabled } from "../../../common/posthog";
import { useChatStore } from "../stores/chatStore";
import EnableChunkingButton from "./EnableChunkingButton";

interface ChatInputStatusProps {
  loading: boolean;
}

type StatusType =
  | "creatingChunks"
  | "chunkingEnabled"
  | "tokenLimitWithChunking"
  | "tokenLimitWithoutChunking"
  | "editing"
  | "default";

const resolveStatusType = (params: {
  isCreatingChunks: boolean;
  isChunkingEnabled: boolean;
  isBlockedByTokenLimit: boolean;
  isChunkingRagEnabled: boolean;
  editingMessage: string | null;
}): StatusType => {
  if (params.isCreatingChunks) {
    return "creatingChunks";
  }

  if (params.isChunkingEnabled) {
    return "chunkingEnabled";
  }

  if (params.isBlockedByTokenLimit && params.isChunkingRagEnabled) {
    return "tokenLimitWithChunking";
  }

  if (params.isBlockedByTokenLimit) {
    return "tokenLimitWithoutChunking";
  }

  if (params.editingMessage) {
    return "editing";
  }

  return "default";
};

interface StatusRendererProps {
  statusType: StatusType;
  onEnableChunking: () => void;
  loading: boolean;
}

const StatusRenderer = ({ statusType, onEnableChunking, loading }: StatusRendererProps): React.ReactElement => {
  if (statusType === "creatingChunks") {
    return (
      <Typography variant="caption" color="info.main">
        Creating chunks...
      </Typography>
    );
  }

  if (statusType === "chunkingEnabled") {
    return (
      <Typography variant="caption" color="success.main">
        &#x2713; Chunked processing enabled - no token limits
      </Typography>
    );
  }

  if (statusType === "tokenLimitWithChunking") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
        <Typography variant="caption" color="error" sx={{ flexShrink: 0 }}>
          Token limit exceeded.
        </Typography>
        <EnableChunkingButton onClick={onEnableChunking} disabled={loading} />
      </Box>
    );
  }

  if (statusType === "tokenLimitWithoutChunking") {
    return (
      <Typography variant="caption" color="error">
        Token limit exceeded. Please adjust your filters or reset chat to continue.
      </Typography>
    );
  }

  if (statusType === "editing") {
    return (
      <Typography variant="caption" sx={{ color: "primary.main" }}>
        Editing message (press ESC to cancel)
      </Typography>
    );
  }

  return (
    <Typography variant="caption" color="text.secondary">
      Press Enter to send, Shift+Enter for new line
    </Typography>
  );
};

const ChatInputStatus = ({ loading }: ChatInputStatusProps): React.ReactElement => {
  const {
    editingMessage,
    isBlockedByTokenLimit,
    isChunkingEnabled,
    enableChunking,
    isCreatingChunks
  } = useChatStore();

  const isChunkingRagEnabled = useFeatureFlagEnabled("chunking-rag");

  const handleEnableChunking = async (): Promise<void> => {
    await enableChunking();
  };

  const statusType = resolveStatusType({
    isCreatingChunks,
    isChunkingEnabled,
    isBlockedByTokenLimit,
    isChunkingRagEnabled,
    editingMessage
  });

  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <StatusRenderer statusType={statusType} onEnableChunking={handleEnableChunking} loading={loading} />
    </Box>
  );
};

export default ChatInputStatus;
